import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ExternalLink, Globe } from 'lucide-react';
import { Button, Input, Select } from '@vd/ui';
import type { Project } from '@vd/types';
import { api } from '../../lib/api';
import { ProjectMark } from './ProjectMark';
import { showIconInList } from './iconLook';
import styles from './ProjectIconField.module.css';

/**
 * A project's icon in its settings (16 Oct): what it shows now, large, then
 * the choice between its picture (an upload, else the site's icon) and its
 * initials, an upload, and a new look for the site's icon. Each saves at
 * once; the project's answers carry the result. A look runs in the worker,
 * so after asking for one the field reads the project again every few
 * seconds until its `iconCheckedAt` moves, for at most `LOOK_WAIT_MS`.
 */
const LOOK_POLL_MS = 3000;
const LOOK_WAIT_MS = 60_000;

/** The value of the choice that opens the field for a site the URL patterns do not name. */
const OTHER_SITE = 'other';

/** A site as it reads in the choice: its host, without the https:// in front. */
const bareSite = (site: string) => site.replace(/^https:\/\//, '');

/**
 * Where the icon is read from (22 Oct, Jason): the sites the URL patterns name,
 * the first one unless the owner picks another, and another site typed in, for
 * a project whose first pattern is not its own site (a pattern added to borrow
 * something from elsewhere). An https address only; the server keeps its origin.
 * Choosing the first pattern's site goes back to following the first pattern
 * (23 Oct, Jason: no separate button for it). The look button sits beside the
 * choice, and the link to the URL patterns under both.
 */
function IconSitePicker({ project, busy, onPick, look }: { project: Project; busy: boolean; onPick: (site: string | null) => void; look: ReactNode }) {
  const sites = project.iconSites ?? [];
  const picked = project.iconSitePicked ?? null;
  const current = project.iconSite ?? null;
  const [typing, setTyping] = useState(false);
  const [typed, setTyped] = useState('');
  const [invalid, setInvalid] = useState(false);
  const choices = picked && !sites.includes(picked) ? [...sites, picked] : sites;
  const urls = `/projects/${project.id}/settings?tab=urls`;
  const close = () => { setTyping(false); setTyped(''); setInvalid(false); };
  const save = () => {
    const value = typed.trim();
    // A bare host gets https in front; an address naming any other scheme is refused, not wrapped.
    const address = /^[a-z][a-z0-9+.-]*:/i.test(value) ? value : `https://${value}`;
    let ok = false;
    try { ok = value.length > 0 && !/[*\s]/.test(value) && new URL(address).protocol === 'https:'; } catch { ok = false; }
    if (!ok) { setInvalid(true); return; }
    close();
    onPick(address);
  };
  if (typing) {
    return (
      <div className={styles.site} data-testid="project-icon-site">
        <Globe size={13} aria-hidden="true" className={styles.siteIcon} />
        <label htmlFor={`icon-site-other-${project.id}`}>Icon from</label>
        <Input id={`icon-site-other-${project.id}`} className={styles.siteInput} value={typed} placeholder="https://example.com" autoFocus
          invalid={invalid} aria-describedby={invalid ? `icon-site-error-${project.id}` : undefined} data-testid="project-icon-site-other"
          onChange={(event) => { setTyped(event.target.value); setInvalid(false); }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') { event.preventDefault(); save(); }
            if (event.key === 'Escape') close();
          }} />
        <Button size="sm" variant="secondary" disabled={busy || typed.trim() === ''} onClick={save}>Use this site</Button>
        <Button size="sm" variant="ghost" onClick={close}>Cancel</Button>
        {invalid && <span id={`icon-site-error-${project.id}`} role="alert" className={styles.siteError}>Enter an https address, such as https://example.com.</span>}
      </div>
    );
  }
  return (
    <div className={styles.site} data-testid="project-icon-site">
      <Globe size={13} aria-hidden="true" className={styles.siteIcon} />
      {choices.length > 0 ? <>
        <label htmlFor={`icon-site-${project.id}`}>Icon from</label>
        <Select id={`icon-site-${project.id}`} className={styles.siteSelect} value={current ?? ''} disabled={busy} data-testid="project-icon-site-choice"
          onChange={(event) => {
            const value = event.target.value;
            if (value === OTHER_SITE) { setTyping(true); return; }
            // The first pattern's site follows the first pattern; any other choice saves as picked,
            // so a pattern added at the top later does not move it.
            if (value && value !== current) onPick(value === sites[0] ? null : value);
          }}>
          {choices.map((site) => (
            <option key={site} value={site}>{bareSite(site)}{site === sites[0] ? ' (first URL pattern)' : ''}</option>
          ))}
          <option value={OTHER_SITE}>Another site…</option>
        </Select>
        {current && (
          <a href={current} target="_blank" rel="noopener noreferrer" className={styles.siteUrl} data-testid="project-icon-site-url" aria-label={`Open ${bareSite(current)}`}>
            <ExternalLink size={11} aria-hidden="true" />
          </a>
        )}
        {look}
        <span className={styles.siteBelow}><Link to={urls} className={styles.siteLink}>Edit URL patterns</Link></span>
      </> : <>
        <span>No site to look at: the URL patterns name no public https site (a local address like localhost has none).</span>
        <Link to={urls} className={styles.siteLink}>Add one in URL patterns</Link>
        <span aria-hidden="true">·</span>
        <button type="button" className={styles.siteLink} disabled={busy} onClick={() => setTyping(true)}>Use another site</button>
      </>}
    </div>
  );
}

export function ProjectIconField({ project }: { project: Project }) {
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // The choice shows at once; the project's answer settles it (and puts it back on a failure).
  const [choice, setChoice] = useState(project.iconChoice ?? 'auto');
  useEffect(() => setChoice(project.iconChoice ?? 'auto'), [project.iconChoice]);
  const initials = choice === 'initials';
  // A look under way: for which project, when it was asked for, and the check it is waiting to see replaced.
  // It says only that the look is done; "Showing" above says what shows (Codex review round 3).
  const [looking, setLooking] = useState<{ projectId: string; since: number; checkedAt: string | null } | null>(null);
  useEffect(() => {
    if (!looking) return undefined;
    if (looking.projectId !== project.id) {
      setLooking(null);
      return undefined;
    }
    if ((project.iconCheckedAt ?? null) !== looking.checkedAt) {
      setLooking(null);
      setNotice("Done looking for the site's icon.");
      // The sidebar's list may have been read just before the look finished: give it what this read found.
      showIconInList(queryClient, project.companyId, project.id, {
        iconUrl: project.iconUrl, iconCheckedAt: project.iconCheckedAt, hasUploadedIcon: project.hasUploadedIcon, iconChoice: project.iconChoice,
      });
      return undefined;
    }
    const timer = setInterval(() => {
      if (Date.now() - looking.since > LOOK_WAIT_MS) {
        setLooking(null);
        setNotice('Still looking. The icon shows here once the site answers.');
        return;
      }
      void queryClient.invalidateQueries({ queryKey: ['project', project.id] });
      void queryClient.invalidateQueries({ queryKey: ['projects', project.companyId] });
    }, LOOK_POLL_MS);
    return () => clearInterval(timer);
  }, [looking, project.iconCheckedAt, project.id, project.companyId, queryClient]);
  const choose = (next: 'auto' | 'initials') => {
    setChoice(next);
    void run('choice', () => api.updateProject(project.id, { iconChoice: next })).then((ok) => { if (!ok) setChoice(project.iconChoice ?? 'auto'); });
  };

  const run = async (what: string, action: () => Promise<unknown>, said?: string) => {
    setBusy(what);
    setError(null);
    setNotice(null);
    try {
      const answer = await action();
      // An answer that carries the icon goes straight into the sidebar's list.
      if (answer && typeof answer === 'object' && 'iconUrl' in answer) {
        showIconInList(queryClient, project.companyId, project.id, answer as Partial<Project>);
      }
      await queryClient.invalidateQueries({ queryKey: ['project', project.id] });
      await queryClient.invalidateQueries({ queryKey: ['projects', project.companyId] });
      if (said) setNotice(said);
      return true;
    } catch (failure) {
      setError((failure as Error).message || 'That did not work. Try again.');
      return false;
    } finally {
      setBusy(null);
    }
  };

  const shown = initials ? 'Initials' : project.hasUploadedIcon ? 'Your upload' : project.iconUrl ? "The site's icon" : 'Initials, until an icon is found';
  return (
    <div className={styles.field} data-testid="project-icon-field">
      <ProjectMark project={{ ...project, iconUrl: initials ? null : project.iconUrl ?? null }} size={48} />
      <div className={styles.controls}>
        <p className={styles.shown}>Showing: {shown}</p>
        <div className={styles.choice} role="radiogroup" aria-label="Icon">
          <label><input type="radio" name={`icon-${project.id}`} checked={!initials} disabled={busy !== null}
            onChange={() => choose('auto')} />Picture</label>
          <label><input type="radio" name={`icon-${project.id}`} checked={initials} disabled={busy !== null}
            onChange={() => choose('initials')} />Initials</label>
        </div>
        <div className={styles.buttons}>
          <input ref={input} type="file" accept="image/png,image/jpeg" hidden data-testid="project-icon-input"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = '';
              if (file) void run('upload', () => api.uploadProjectIcon(project.id, file), 'Icon uploaded.');
            }} />
          <Button variant="secondary" size="sm" loading={busy === 'upload'} disabled={busy !== null} onClick={() => input.current?.click()}>
            {project.hasUploadedIcon ? 'Replace upload' : 'Upload an icon'}
          </Button>
          {project.hasUploadedIcon && (
            <Button variant="ghost" size="sm" loading={busy === 'remove'} disabled={busy !== null}
              onClick={() => void run('remove', () => api.removeProjectIcon(project.id), "Upload removed. The site's icon shows when there is one.")}>Remove upload</Button>
          )}
        </div>
        {/* Which site the look reads (Jason, 16 Oct), and a choice of it (22 Oct): any site the URL patterns name, or another. */}
        <IconSitePicker project={project} busy={busy !== null}
          look={(
            <Button variant="ghost" size="sm" loading={busy === 'look' || looking !== null} disabled={busy !== null || looking !== null || !project.iconSite}
              onClick={() => {
                const checkedAt = project.iconCheckedAt ?? null;
                const projectId = project.id;
                void run('look', () => api.refreshProjectIcon(projectId), "Looking for the site's icon.").then((ok) => { if (ok) setLooking({ projectId, since: Date.now(), checkedAt }); });
              }}>
              Look for the site's icon again
            </Button>
          )}
          onPick={(iconSite) => {
            const checkedAt = project.iconCheckedAt ?? null;
            const projectId = project.id;
            // The save asks for a look at the new site; the field waits for it as "Look again" does.
            void run('site', () => api.updateProject(projectId, { iconSite }), "Looking for that site's icon.").then((ok) => { if (ok) setLooking({ projectId, since: Date.now(), checkedAt }); });
          }} />
        <p className={styles.help}>PNG or JPEG uploads, up to 2 MB; square pictures look best.</p>
        {notice && <p role="status" className={styles.notice}>{notice}</p>}
        {error && <p role="alert" className={styles.error}>{error}</p>}
      </div>
    </div>
  );
}
